import { useEffect, useRef } from 'react'

import type { CreatorIntegrationsApi } from '@fourthwall/services/dist/CreatorIntegrationsApiClient'

import { useCreateStoreMutation } from '../queries/useCreateStoreMutation'
import { useMailchimpConfigurationQuery } from '../queries/useMailchimpConfigurationQuery'
import { useMailchimpCreateSyncMutation } from '../queries/useMailchimpCreateSyncMutation'
import { useMailchimpSyncStatusQuery } from '../queries/useMailchimpSyncStatusQuery'
import { useRefreshListsMutation } from '../queries/useRefreshListsMutation'

export const useMailchimp = () => {
  const { mailchimpConfigurationQuery, invalidateMailchimpConfigurationQuery } =
    useMailchimpConfigurationQuery()
  const { mailchimpSyncStatusQuery, invalidateMailchimpSyncStatusQuery } =
    useMailchimpSyncStatusQuery()
  const { createMailchimpSyncMutation } = useMailchimpCreateSyncMutation()
  const { refreshMailchimpListsMutation } = useRefreshListsMutation()
  const { createMailchimpStoreMutation } = useCreateStoreMutation()
  const pollRef = useRef<NodeJS.Timeout | null>(null)

  const refreshLists = async () => {
    try {
      await refreshMailchimpListsMutation.mutateAsync([])
      invalidateMailchimpConfigurationQuery()
    } catch (error) {
      console.error('Failed to refresh Mailchimp lists:', error)
      throw error
    }
  }

  const createSyncJob = async (
    requestBody: CreatorIntegrationsApi.CreateMailchimpSyncJob.RequestBody,
  ) => {
    try {
      await createMailchimpSyncMutation.mutateAsync([requestBody])
    } catch (error) {
      console.error('Failed to create Mailchimp sync job:', error)
      throw error
    }

    invalidateMailchimpSyncStatusQuery()
  }

  const createStore = async (listId: string | undefined) => {
    try {
      await createMailchimpStoreMutation.mutateAsync([{ listId }])
      invalidateMailchimpConfigurationQuery()
    } catch (error) {
      console.error('Failed to create Mailchimp store:', error)
      throw error
    }
  }

  useEffect(() => {
    if (mailchimpSyncStatusQuery.data?.activeSync) {
      pollRef.current = setInterval(() => {
        invalidateMailchimpSyncStatusQuery()
      }, 1000)
    } else if (pollRef.current) {
      console.warn('Clearing timeout')
      clearInterval(pollRef.current)
    }
  }, [mailchimpSyncStatusQuery.data?.activeSync])

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
      }
    }
  }, [])

  const progress = [
    mailchimpSyncStatusQuery.data?.members
      ? { key: 'MEMBERS' as const, ...mailchimpSyncStatusQuery.data.members }
      : null,
    mailchimpSyncStatusQuery.data?.newsletter
      ? { key: 'NEWSLETTER' as const, ...mailchimpSyncStatusQuery.data.newsletter }
      : null,
    mailchimpSyncStatusQuery.data?.offers
      ? { key: 'PRODUCTS' as const, ...mailchimpSyncStatusQuery.data.offers }
      : null,
    mailchimpSyncStatusQuery.data?.promotions
      ? { key: 'PROMOTIONS' as const, ...mailchimpSyncStatusQuery.data.promotions }
      : null,
  ]
    .filter((item): item is NonNullable<typeof item> => item != null && item.total != null)
    .map((item) => ({
      key: item.key,
      progress: item.progress ?? 0,
      total: item.total,
    }))

  return {
    mailchimp: {
      configuration: mailchimpConfigurationQuery.data,
      isLoading: mailchimpConfigurationQuery.isLoading,
    },
    syncStatus: {
      active: mailchimpSyncStatusQuery.data?.activeSync || false,
      recent: mailchimpSyncStatusQuery.data?.recentSync || false,
      hasSynced: mailchimpSyncStatusQuery.data?.hasSync || false,
      progress,
      isLoading: mailchimpSyncStatusQuery.isLoading,
    },
    refreshLists,
    isRefreshing: refreshMailchimpListsMutation.isLoading,
    createStore,
    isCreatingStore: createMailchimpStoreMutation.isLoading,
    createSyncJob,
    isCreatingSyncJob: createMailchimpSyncMutation.isLoading,
  }
}
